import styled from "styled-components";

import { IconWeight } from "../Icon/Icon";
import { SpaceProps, space, GridProps, grid, LayoutProps } from "styled-system";
import { AnalyticsProps } from "../analytics.types";
import CardDecider from "./CardDecider";

export interface FeaturedCardAnalyticsProps extends AnalyticsProps {
  eventCategory?: string;
  eventLabel?: string;
}

export type FeaturedCardVariant =
  | "FullImageCard"
  | "FullImageSmallCard"
  | "HalfImageCard"
  | "HalfImageWideCard"
  | "NoImageCard"
  | "NoImageWideCard"
  | "IconCard"
  | "IconWideCard";

export interface FeaturedCardContent {
  id: string;
  linkUrl: string;
  shouldOpenInNewWindow?: boolean;
  icon?: {
    name: string;
    weight: IconWeight;
    color?: string;
  };
  image?: { url: string; alt: string; overlay?: 0 | 0.25 | 0.5 | 0.75 };
  headline?: string;
  description?: string;
  moreInfo?: {
    label: string;
    visible: boolean;
  };
  analytics?: AnalyticsProps;
}

export interface FeaturedCardProps extends GridProps {
  /**
  - The variant of the card. Accepts one of the following values: 
  - FullImageCard
  - FullImageSmallCard
  - HalfImageCard
  - HalfImageWideCard
  - NoImageCard
  - NoImageWideCard
  - IconWideCard
  */
  variant: FeaturedCardVariant;

  /**
  - Represents the content of the card. Accepts a json object with the following props:
  - linkUrl: string;
  - shouldOpenInNewWindow?: boolean;
  - icon?: {
    name: "map-signs";
    weight: "regular" | "solid" | "light";
    color? : string;
  };
  - image?: { url: string; alt: string; overlay?: 0 | 0.25 | 0.5 | 0.75 };
  - headline?: string;
  - description?: string;
  - name?: string;
  - moreInfo?: {
    label: string;
    visible: boolean;
  };
  - analytics?: AnalyticsProps; //Used for adobe tracking. Accepts a json object with the following props:
     - eventAction?: string; // Name assigned to the web page element, whose users' interaction you want to track.
     - eventSection?: string; // Name of the page section in which the element will sit.
     - eventType?: string; // Name assigned to categorise the action/label further to give more detail.
     - eventValue?: string; // The numerical value assigned to the event you want to track.
   */
  content: FeaturedCardContent;
  /**
  Allows the user to specify the horizontal margin of the card. Accepts values from the space property in the theme(numbers 0-9 that, multiplied by 4, represent the number of pixels), strings corresponding to the number of pixels or arrays(string[] | number[]) for responsive values.
  (eg. marginX: 1 -> margin-left: 4px, margin-right: 4px;
      marginX: 2 -> margin-left: 8px, margin-right: 8px;
      marginX: "1px" -> margin-left: 1px, margin-right: 1px;
      marginX: "2px" -> margin-left: 2px, margin-right: 2px;
      marginX: [1,2] -> margin-left: 4px, margin-right: 4px up to the first breakpoint and margin-left: 8px, margin-right: 8px to the second;)
  */
  marginX?: number | string | (string | number)[];
  /**
 Allows the user to specify the vertical margin of the card. Accepts values from the space property in the theme(numbers 0-9 that, multiplied by 4, represent the number of pixels), strings corresponding to the number of pixels or arrays(string[] | number[]) for responsive values.
 (eg. marginY: 1 -> margin-top: 4px, margin-bottom: 4px;
     marginY: 2 -> margin-top: 8px, margin-bottom: 8px;
     marginY: "1px" -> margin-top: 1px, margin-bottom: 1px;
     marginY: "2px" -> margin-top: 2px, margin-bottom: 2px;
     marginY: [1,2] -> margin-left: 4px, margin-right: 4px up to the first breakpoint and margin-left: 8px, margin-right: 8px to the second;)
 */
  marginY?: number | string | (string | number)[];
  width?: LayoutProps["width"];
  height?: LayoutProps["height"];
  css?: string;
}

const Anchor = styled.a<SpaceProps & GridProps & { css?: string }>`
  display: inline-block;
  text-decoration: none;
  ${space}
  ${grid}
  ${(props) => props.css}
`;

export default function FeaturedCard({
  variant,
  content,
  marginX = 0,
  marginY = 0,
  css,
  width,
  height,
  ...rest
}: FeaturedCardProps) {
  const processedHeadline =
    content.headline && content.headline.length > 250
      ? content.headline.slice(0, 250)
      : content.headline;
  const eventLabel =
    processedHeadline?.toLowerCase().replace(/\s+/g, "-") || "";
  const analytics: FeaturedCardAnalyticsProps = {
    ...content.analytics,
    eventCategory: "card",
    eventLabel: eventLabel,
  };

  return (
    <Anchor
      href={content.linkUrl}
      target={content.shouldOpenInNewWindow ? "_blank" : "_top"}
      aria-label="featured card"
      marginX={marginX}
      marginY={marginY}
      data-en="click"
      data-ec={analytics.eventCategory}
      data-el={analytics.eventLabel}
      data-ea={analytics.eventAction}
      data-es={analytics.eventSection}
      data-et={analytics.eventType}
      data-ev={analytics.eventValue}
      css={css}
      {...rest}
    >
      <CardDecider
        variant={variant}
        content={content}
        width={width}
        height={height}
      />
    </Anchor>
  );
}
